import { useCallback, useEffect, useMemo, useRef } from 'react';
import { layoutFolderTree } from '../layoutTree';
import { groupDocsForPeek, trimPeekGroups } from '../groupDocs';
import { DEMO_FOLDERS, DEMO_DOCS_BY_FOLDER } from '../demoData';
import { ATLAS_UNFILED_ID, isAtlasUnfiledId } from '../constants';
import { isAdminOnlyDoc } from '../../../../utils/docTags';
import { useAuth } from '../../../AuthProvider';

/**
 * Live folder list + laid-out tree + peek payload for Atlas.
 */
export function useAtlasTree({
  organization,
  docs,
  useDemo,
  expandedId,
  fitToBounds,
  nodeHeights = null,
}) {
  const { role } = useAuth();
  const canViewAdminDocs = (role ?? 'admin') !== 'user';
  const hasFittedRef = useRef(false);
  const orgReady = useDemo || Boolean(organization?.ready);
  const treeReady = orgReady;

  const unfiledDocs = useMemo(() => {
    if (useDemo) return [];
    const docFolders = organization?.docFolders || {};
    return docs.filter((d) => {
      if (!canViewAdminDocs && isAdminOnlyDoc(d)) return false;
      return !docFolders[d.id];
    });
  }, [useDemo, organization?.docFolders, docs, canViewAdminDocs]);

  const folders = useMemo(() => {
    if (useDemo) return DEMO_FOLDERS;
    if (!treeReady) return [];
    const list = organization?.folders || [];
    const docFolders = organization?.docFolders || {};
    const counts = {};
    for (const fid of Object.values(docFolders)) {
      counts[fid] = (counts[fid] || 0) + 1;
    }
    const real = list.map((f) => ({
      ...f,
      docCount: counts[f.id] || 0,
    }));
    if (unfiledDocs.length === 0) return real;
    return [
      {
        id: ATLAS_UNFILED_ID,
        name: 'Unfiled Documents',
        parentId: null,
        docCount: unfiledDocs.length,
        kind: 'unfiled',
      },
      ...real,
    ];
  }, [useDemo, treeReady, organization?.folders, organization?.docFolders, unfiledDocs.length]);

  const { nodes, edges, bounds, clusters } = useMemo(
    () => layoutFolderTree(folders, { heights: nodeHeights }),
    [folders, nodeHeights]
  );

  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const onlyUnfiled =
    nodes.length === 1 && isAtlasUnfiledId(nodes[0]?.id);

  useEffect(() => {
    if (nodes.length === 0) {
      hasFittedRef.current = false;
      return;
    }
    if (onlyUnfiled) {
      fitToBounds(bounds, 48);
      hasFittedRef.current = true;
      return;
    }
    if (hasFittedRef.current) return;
    hasFittedRef.current = true;
    fitToBounds(bounds, 48);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes.length > 0, onlyUnfiled]);

  const docsInFolder = useCallback(
    (folderId) => {
      if (isAtlasUnfiledId(folderId)) return unfiledDocs;
      if (useDemo) return DEMO_DOCS_BY_FOLDER[folderId] || [];
      const docFolders = organization?.docFolders || {};
      return docs.filter((d) => {
        if (!canViewAdminDocs && isAdminOnlyDoc(d)) return false;
        return docFolders[d.id] === folderId;
      });
    },
    [useDemo, organization?.docFolders, docs, canViewAdminDocs, unfiledDocs]
  );

  const peek = useMemo(() => {
    if (!expandedId) return null;
    const node = nodeById.get(expandedId);
    if (!node) return null;
    const folderDocs = docsInFolder(expandedId);
    const groups = groupDocsForPeek(folderDocs);
    const trimmed = trimPeekGroups(groups);
    return {
      folderId: expandedId,
      browseFolderId: isAtlasUnfiledId(expandedId) ? null : expandedId,
      kind: node.kind || 'folder',
      ...trimmed,
      empty: folderDocs.length === 0,
    };
  }, [expandedId, nodeById, docsInFolder]);

  return { folders, nodes, edges, bounds, clusters, nodeById, peek, docsInFolder, treeReady };
}
